import { useMemo } from 'react';
import { Node } from '@xyflow/react';

import { MagicModel } from '@/types/MagicModel';

import { GeneralSettingsType } from '../SettingsSidebar/types';
import { findLanguageAnyNodes } from '../util/findLanguageAnyNodes';

interface SelectedSensors {
  buttons: string[];
  buzzers: string[];
  trihows: string[];
}

interface SensorAvailabilityReturn {
  canAddMultipleTouchSensors: boolean;
  availableHardware: {
    hasAvailableTrihows: boolean;
    hasAvailableButtons: boolean;
    hasAvailableBuzzers: boolean;
  };
  sensorNodeAvailability: {
    'sensor-nfcSpecific': boolean;
    'sensor-button': boolean;
    'sensor-buzzer': boolean;
    'sensor-touch': boolean;
  };
  availableCounts: {
    trihows: number;
    buttons: number;
    buzzers: number;
  };
}

export function useSensorAvailability(
  nodes: Node[],
  settings: GeneralSettingsType,
  selectedSensors: SelectedSensors,
  magicModels: MagicModel[],
): SensorAvailabilityReturn {
  // Get all language-any sensors in the current nodes
  const {
    languageAnyTrihows,
    languageAnyButtons,
    languageAnyBuzzers,
    languageAnyTouches,
  } = findLanguageAnyNodes(nodes);

  const canAddMultipleTouchSensors = useMemo(() => {
    const hasTouchNode = nodes.some((node) => node.type === 'sensor-touch');
    return !hasTouchNode;
  }, [nodes]);

  const hasNonLanguageAnyButtons = useMemo(
    () =>
      selectedSensors.buttons.filter(
        (btn) =>
          !languageAnyButtons.some((anyBtn) =>
            anyBtn.data.options.buttons?.some((b) => b.triggerId === btn),
          ),
      ).length > 0,
    [selectedSensors.buttons, languageAnyButtons],
  );

  const hasNonLanguageAnyTrihows = useMemo(
    () =>
      selectedSensors.trihows.filter(
        (th) =>
          !languageAnyTrihows.some((AnyTh) =>
            AnyTh.data.options.trihow?.some((t) => t.triggerId === th),
          ),
      ).length > 0,
    [selectedSensors.trihows, languageAnyTrihows],
  );

  const hasNonLanguageAnyBuzzers = useMemo(
    () =>
      selectedSensors.buzzers.filter(
        (bzz) =>
          !languageAnyBuzzers.some((AnyBzz) =>
            AnyBzz.data.options.buzzers?.some((b) => b.triggerId === bzz),
          ),
      ).length > 0,
    [selectedSensors.buzzers, languageAnyBuzzers],
  );

  const availableCounts = useMemo(
    () => ({
      trihows: magicModels.length - selectedSensors.trihows.length,
      buttons: settings.buttons.length - selectedSensors.buttons.length,
      buzzers: settings.buzzers.length - selectedSensors.buzzers.length,
    }),
    [
      magicModels.length,
      settings.buttons.length,
      settings.buzzers.length,
      selectedSensors.trihows.length,
      selectedSensors.buttons.length,
      selectedSensors.buzzers.length,
    ],
  );

  const availableHardware = useMemo(
    () => ({
      hasAvailableTrihows: availableCounts.trihows > 0,
      hasAvailableButtons: availableCounts.buttons > 0,
      hasAvailableBuzzers: availableCounts.buzzers > 0,
    }),
    [availableCounts],
  );

  // When laguage selective is active, sensors can be used multiple times
  const sensorNodeAvailability = useMemo(
    () => ({
      'sensor-nfcSpecific':
        availableHardware.hasAvailableTrihows ||
        (settings.languageSelective &&
          magicModels.length > 0 &&
          hasNonLanguageAnyTrihows),
      'sensor-button':
        availableHardware.hasAvailableButtons ||
        (settings.languageSelective &&
          settings.buttons.length > 0 &&
          hasNonLanguageAnyButtons),
      'sensor-buzzer':
        availableHardware.hasAvailableBuzzers ||
        (settings.languageSelective &&
          settings.buzzers.length > 0 &&
          hasNonLanguageAnyBuzzers),
      'sensor-touch':
        (canAddMultipleTouchSensors ||
          (settings.languageSelective && languageAnyTouches.length === 0)) &&
        Object.values(settings.screens).some((screen) => screen.touchEnabled),
    }),
    [
      availableHardware,
      canAddMultipleTouchSensors,
      settings.languageSelective,
      settings.screens,
      settings.buttons.length,
      settings.buzzers.length,
      magicModels.length,
      hasNonLanguageAnyButtons,
      hasNonLanguageAnyTrihows,
      hasNonLanguageAnyBuzzers,
      languageAnyTouches.length,
    ],
  );

  return {
    canAddMultipleTouchSensors,
    availableHardware,
    sensorNodeAvailability,
    availableCounts,
  };
}
